import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useBookContentWarnings } from '@/hooks/useBookContentWarnings';
import { useAuth } from '@/hooks/useAuth';
import { ContentWarningManager } from './ContentWarningManager';

interface ContentWarningsSectionProps {
  bookId: string;
  userHasRead?: boolean;
}

export function ContentWarningsSection({ bookId, userHasRead = false }: ContentWarningsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const { warningsWithCounts, totalWarningCount, isLoading } = useBookContentWarnings(bookId);

  if (isLoading) return null;
  
  const hasWarnings = totalWarningCount > 0;
  const canAddWarnings = user && userHasRead;

  if (!hasWarnings && !canAddWarnings) return null;

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between px-4 py-3 h-auto hover:bg-amber-500/10"
          >
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">Content Warnings</span>
              {hasWarnings && (
                <span className="text-sm text-muted-foreground">
                  ({totalWarningCount} {totalWarningCount === 1 ? 'type' : 'types'} flagged)
                </span>
              )}
            </div>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="px-4 pb-4">
          {hasWarnings ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-3">
                {warningsWithCounts.reduce((sum, w) => sum + w.count, 0)} reader{warningsWithCounts.reduce((sum, w) => sum + w.count, 0) !== 1 ? 's have' : ' has'} flagged content in this book
              </p>
              <ul className="space-y-1.5">
                {warningsWithCounts.map((warning) => (
                  <li key={warning.warning_name} className="flex items-center gap-2 text-sm">
                    <span className="text-amber-600 dark:text-amber-400">•</span>
                    <span className="text-foreground">{warning.warning_name}</span>
                    <span className="text-muted-foreground">
                      ({warning.count} reader{warning.count !== 1 ? 's' : ''})
                    </span>
                    {warning.userHasAdded && (
                      <span className="text-xs text-primary">(you)</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No content warnings have been added yet.
            </p>
          )}

          {canAddWarnings && (
            <div className="mt-4 pt-3 border-t border-border/50">
              <ContentWarningManager bookId={bookId} />
            </div>
          )}

          {!user && (
            <p className="text-xs text-muted-foreground mt-3">
              Sign in and mark this book as read to add content warnings.
            </p>
          )}

          {user && !userHasRead && (
            <p className="text-xs text-muted-foreground mt-3">
              Mark this book as read to add content warnings.
            </p>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
